import styles from './customSpacedrawer.module.scss';
import Drawer from '@mui/material/Drawer';
import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, TextField } from '@mui/material';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import ConfirmModal from 'components/common/ConfirmModal/ConfirmModal';
import { useDispatch } from 'react-redux';
import actions from 'store/actions';
import { useValidator } from 'hooks/useValidator';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import { useTranslation } from 'react-i18next';
import { getUnits } from 'utils/units';
import { addThousandSeparator } from 'utils/numbers';
import { getFormatSeparatorsOptions } from 'config/marketConfig';

const DRAWER_ANCHOR_TYPE = 'right';

export default function CustomSpacedrawer(props) {
  const { disabled, isExternalAccess, isEdit } = props;
  const { quantityMinimum, quantityMaximum } = props.quantityBounds;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [openModal, setOpenModal] = useState(false);
  const [customSpace, setCustomSpace] = useState(props.customSpace);

  const state = useMemo(() => props.state, [props]);
  const totalSpace = useMemo(
    () =>
      addThousandSeparator(
        customSpace.quantity * customSpace.width * customSpace.height,
        getFormatSeparatorsOptions(),
      ) +
      ' ' +
      t('step5.spaceDrawer.total') +
      ' ' +
      getUnits().area,
    [customSpace, t],
  );

  const [validables, validator] = useValidator(['name', 'width', 'height', 'quantity'], () => {
    const message = {};
    if (!customSpace.name) message.name = t('step5.spaceDrawer.error1');
    if (customSpace.width < 1) message.width = t('step5.spaceDrawer.error2');
    if (customSpace.height < 1) message.height = t('step5.spaceDrawer.error3');
    if (customSpace.quantity < 1) message.quantity = t('step5.spaceDrawer.error4');
    return message;
  });

  useEffect(() => {
    setCustomSpace(props.customSpace);
  }, [props]);

  const handleCancel = () => {
    const dirty =
      props.customSpace.name !== customSpace.name ||
      props.customSpace.width !== customSpace.width ||
      props.customSpace.height !== customSpace.height ||
      props.customSpace.quantity !== customSpace.quantity;

    if (dirty) setOpenModal(true);
    else {
      validator.reset();
      props.toggleDrawer(false);
    }
  };

  const handleSaveChanges = () => {
    if (!validator.validate()) return;

    dispatch(actions.na.updateCustomSpace({ ...customSpace }));
    dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: true }) : updateNA);

    setOpenModal(false);
    props.toggleDrawer(false);
  };

  const handleModalAction = (confirmIgnore) => {
    setOpenModal(false);
    if (confirmIgnore) {
      validator.reset();
      props.toggleDrawer(false);
    }
  };

  return (
    <Drawer
      data-testid="CustomSpaceDrawer"
      anchor={DRAWER_ANCHOR_TYPE}
      open={state}
      onClose={() => props.toggleDrawer(false)}>
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.titles}>
            <div className={styles.first_title}>{t('step5.heading')}</div>
            <div data-testid="CustomSpaceTitle" className={styles.second_title}>
              {t(`step5.spaceDrawer.${isEdit ? 'editCustomSpaceTitle' : 'createTitle'}`)}
            </div>
          </div>
          <IconButton
            data-testid="CustomSpaceCancleIcon"
            onClick={handleCancel}
            className={styles.close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.description}>{t('step5.spaceDrawer.createDescription')}</div>
        <div className={styles.spaces}>
          <div className={styles.title}>{t('step5.spaceDrawer.name')}*</div>
          <div>
            <TextField
              disabled={disabled}
              data-testid="CustomSpaceName"
              error={!validables.name.valid}
              helperText={validables.name.message}
              className={styles.input_box}
              value={customSpace.name}
              placeholder={t('step5.spaceDrawer.namePlaceholder')}
              onChange={(event) => setCustomSpace({ ...customSpace, name: event.target.value })}
            />
          </div>
          <div className={styles.title}>{t('step5.spaceDrawer.width')}*</div>
          <div>
            <TextField
              disabled={disabled}
              data-testid="CustomSpaceWidth"
              error={!validables.width.valid}
              helperText={validables.width.message}
              className={styles.input_box}
              value={customSpace.width}
              onChange={(event) => setCustomSpace({ ...customSpace, width: event.target.value })}
              inputProps={{
                min: 0,
                max: 10000,
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
              }}
            />
          </div>
          <div className={styles.title}>{t('step5.spaceDrawer.length')}*</div>
          <div>
            <TextField
              disabled={disabled}
              data-testid="CustomSpaceHeight"
              error={!validables.height.valid}
              helperText={validables.height.message}
              className={styles.input_box}
              value={customSpace.height}
              onChange={(event) => setCustomSpace({ ...customSpace, height: event.target.value })}
              inputProps={{
                min: 0,
                max: 10000,
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
              }}
            />
          </div>
          <div className={styles.title}>{t('step5.spaceDrawer.quantity')}</div>
          <div className={styles.controls}>
            <IconButton
              data-testid="CustomSpaceDisableButton"
              disabled={Number(customSpace.quantity) <= quantityMinimum || disabled}
              onClick={() =>
                setCustomSpace({
                  ...customSpace,
                  quantity: customSpace.quantity - 1,
                })
              }
              className={styles.control_button}>
              <RemoveIcon />
            </IconButton>
            <TextField
              disabled={disabled}
              data-testid="CustomSpaceQuantity"
              error={!validables.quantity.valid}
              helperText={validables.quantity.message}
              className={styles.input_space}
              value={customSpace.quantity}
              onChange={(event) =>
                setCustomSpace({
                  ...customSpace,
                  quantity: Number(event.target.value),
                })
              }
              inputProps={{
                style: {
                  textAlign: 'center',
                },
                min: quantityMinimum,
                max: quantityMaximum,
              }}
              InputProps={{
                inputComponent: NumberFormatCustom,
              }}
            />
            <IconButton
              data-testid="CustomSpaceQuantityDisableButton"
              disabled={Number(customSpace.quantity) >= quantityMaximum || disabled}
              onClick={() =>
                setCustomSpace({
                  ...customSpace,
                  quantity: customSpace.quantity + 1,
                })
              }
              className={styles.control_button}>
              <AddIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.summary}>
          <div className={styles.total_input_title}>{t('step5.spaceDrawer.totalSpace')}</div>
          <TextField
            data-testid="CustomSpacetotalSpace"
            disabled
            className={styles.total_input_box}
            value={totalSpace}
            inputProps={{ className: styles.total_input_box }}
          />
          <div className={styles.buttons}>
            <Button
              data-testid="CustomSpaceCancleBtn"
              className={styles.cancel_button}
              size="small"
              variant="contained"
              onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={disabled}
              data-testid="CustomSpaceSaveBtn"
              className={styles.save_button}
              size="small"
              variant="contained"
              onClick={handleSaveChanges}>
              {t('step5.spaceDrawer.save')}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmModal
        data-testid="CustomSpaceConfirmModal"
        title={t('step5.spaceDrawer.confirmationalTitle')}
        description={t('step5.spaceDrawer.confirmationalDescription')}
        open={openModal}
        onAction={handleModalAction}
      />
    </Drawer>
  );
}
